import { useEffect, useState } from 'react';
import {
  MoonIcon,
  SunIcon,
  PaletteIcon,
  CheckIcon,
  SparkleIcon,
} from '@phosphor-icons/react'
import { SidebarTheme } from '@scrambl/shared';
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu"
import { SIDEBAR_THEMES, useUIPreferencesStore } from "@/store/ui-preferences.store"
import { useThemePreferences } from "@/hooks/useThemePreferences"
import { cn } from "@/lib/utils"
import { isTauri } from '@/lib/utils/tauri';

interface ModeToggleProps {
  showSidebarTheme?: boolean
  showGlassToggle?: boolean
}

export function ModeToggle({
  showSidebarTheme = true,
  showGlassToggle: showGlassToggleProp = true,
}: ModeToggleProps) {
  const {
    themeMode,
    sidebarTheme,
    currentSidebarColor,
    setThemeMode,
    setSidebarTheme,
  } = useThemePreferences();
  const tauriGlassEnabled = useUIPreferencesStore((state) => state.tauriGlassEnabled);
  const setTauriGlassEnabled = useUIPreferencesStore((state) => state.setTauriGlassEnabled);
  const showGlassToggle = showGlassToggleProp && isTauri();
  const [isGlassSupported, setIsGlassSupported] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!showGlassToggle) {
      setIsGlassSupported(false);
      return () => {
        cancelled = true;
      };
    }

    const checkSupport = async () => {
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        const supported = await invoke<boolean>('plugin:liquid-glass|is_glass_supported');
        if (!cancelled) {
          setIsGlassSupported(Boolean(supported));
        }
      } catch {
        if (!cancelled) {
          setIsGlassSupported(false);
        }
      }
    };

    void checkSupport();

    return () => {
      cancelled = true;
    };
  }, [showGlassToggle]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 rounded-full hover:bg-muted/50 transition-colors"
          aria-label="Toggle theme"
        >
          <SunIcon className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <MoonIcon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Appearance
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => setThemeMode("light")}
          className={themeMode === "light" ? "bg-accent/50" : ""}
        >
          <SunIcon className="mr-2 h-4 w-4" />
          Light
          {themeMode === "light" && <CheckIcon className="ml-auto h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setThemeMode("dark")}
          className={themeMode === "dark" ? "bg-accent/50" : ""}
        >
          <MoonIcon className="mr-2 h-4 w-4" />
          Dark
          {themeMode === "dark" && <CheckIcon className="ml-auto h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setThemeMode("system")}
          className={themeMode === "system" ? "bg-accent/50" : ""}
        >
          <div className="mr-2 h-4 w-4 flex items-center justify-center">
            <div className="h-3 w-3 rounded-full border-2 border-current" />
          </div>
          System
          {themeMode === "system" && <CheckIcon className="ml-auto h-4 w-4" />}
        </DropdownMenuItem>

        {showGlassToggle && isGlassSupported && (
          <DropdownMenuItem
            onClick={() => setTauriGlassEnabled(!tauriGlassEnabled)}
            onSelect={(event) => event.preventDefault()}
            className="cursor-default"
          >
            <SparkleIcon className="mr-2 h-4 w-4" />
            Glass
            <Switch
              checked={tauriGlassEnabled}
              onCheckedChange={setTauriGlassEnabled}
              className="ml-auto"
              onClick={(event) => event.stopPropagation()}
              aria-label="Toggle glass effect"
            />
          </DropdownMenuItem>
        )}

        {showSidebarTheme && (
          <>
            <DropdownMenuSeparator />

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <PaletteIcon className="mr-2 h-4 w-4" />
                <span>Theme</span>
                <div
                  className="ml-auto h-4 w-4 overflow-hidden rounded-full border border-border shadow-sm"
                  style={{ backgroundColor: currentSidebarColor }}
                />
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="min-w-[180px]">
                {SIDEBAR_THEMES.map((themeOption) => (
                  <DropdownMenuItem
                    key={themeOption.id}
                    onClick={() =>
                      setSidebarTheme(themeOption.id as SidebarTheme)
                    }
                    className={cn(
                      "flex items-center gap-2",
                      sidebarTheme === themeOption.id && "bg-accent/50"
                    )}
                  >
                    {/* Show both light and dark colors side by side */}
                    <div className="flex h-5 w-5 flex-shrink-0 overflow-hidden rounded-full border border-border shadow-sm">
                      <div
                        className="h-full w-1/2"
                        style={{ backgroundColor: themeOption.lightColor }}
                      />
                      <div
                        className="h-full w-1/2"
                        style={{ backgroundColor: themeOption.darkColor }}
                      />
                    </div>
                    <span className="text-sm">{themeOption.name}</span>
                    {sidebarTheme === themeOption.id && (
                      <CheckIcon className="ml-auto h-4 w-4" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
